import { useQuery } from "@tanstack/react-query";
import { api, FESTIVAL_META } from "@/lib/api";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Play, Music } from "lucide-react";

function StatusPill({ status, points }) {
  if (status === "hit") return <span className="text-emerald-400 text-xs font-semibold" data-testid="status-hit">🟢 HIT +{points}</span>;
  if (status === "miss") return <span className="text-red-400 text-xs font-semibold" data-testid="status-miss">🔴 MISS</span>;
  return <span className="text-zinc-500 text-xs" data-testid="status-pending">⚪ AFVENTER</span>;
}

function SlotRow({ slot, artist, testId, subtitle }) {
  const [open, setOpen] = useState(false);
  const dotCls = slot.status === "hit" ? "dot dot-hit" : slot.status === "miss" ? "dot dot-miss" : "dot dot-pending";
  return (
    <>
      <div className="px-6 py-3 flex items-center justify-between gap-3" data-testid={testId}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className={dotCls} />
          {artist?.image_url ? (
            <img src={artist.image_url} className="w-8 h-8 rounded object-cover flex-shrink-0" alt="" />
          ) : (
            <div className="w-8 h-8 rounded bg-zinc-800 flex items-center justify-center flex-shrink-0"><Music size={12} className="text-zinc-600" /></div>
          )}
          <div className="min-w-0">
            <div className="font-medium truncate">{slot.artist_name}</div>
            <div className="text-xs text-zinc-500 capitalize truncate">{subtitle || slot.chosen_category}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {artist?.spotify_id && (
            <button onClick={() => setOpen(v => !v)} className={`p-1.5 rounded hover:bg-white/5 ${open ? "text-emerald-400" : "text-zinc-500"}`} aria-label="Preview" data-testid={`${testId}-play`}><Play size={14} /></button>
          )}
          <StatusPill status={slot.status} points={slot.points} />
        </div>
      </div>
      {open && artist?.spotify_id && (
        <iframe src={`https://open.spotify.com/embed/artist/${artist.spotify_id}?theme=0`} width="100%" height="152" frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" title={artist.name} className="border-b border-white/5" data-testid={`${testId}-iframe`} />
      )}
    </>
  );
}

export default function MyPredictions() {
  const { data: festData } = useQuery({ queryKey: ["festivals"], queryFn: () => api.get("/festivals").then(r => r.data) });
  const { data: scores } = useQuery({ queryKey: ["my-scores"], queryFn: () => api.get("/scores/mine").then(r => r.data) });
  const { data: artists } = useQuery({ queryKey: ["all-artists"], queryFn: () => api.get("/artists", { params: { limit: 5000 } }).then(r => r.data) });
  const artistMap = Object.fromEntries((artists || []).map(a => [a.artist_id, a]));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="label-eyebrow">Dine valg</div>
      <h1 className="hero-title text-4xl sm:text-5xl mt-2">Mine forudsigelser</h1>

      <div className="mt-10 space-y-6">
        {festData?.festivals.map(f => {
          const meta = FESTIVAL_META[f.id];
          const s = scores?.festivals?.[f.id];
          const slots = s?.slots || [];
          return (
            <div key={f.id} className="card-flat overflow-hidden" data-testid={`my-fest-${f.id}`}>
              <Link to={`/festival/${f.id}`} className="flex items-center justify-between px-6 py-5" style={{ borderLeft: `3px solid ${meta.color}` }}>
                <div>
                  <div className="font-display font-bold text-2xl">{f.name}</div>
                  <div className="text-sm text-zinc-500 mt-1">
                    {s?.hits ?? 0} hits · {s?.headliner_hits ?? 0}/{f.slots.headliner} headliner · {s?.international_hits ?? 0}/{f.slots.international} intl · {s?.danish_hits ?? 0}/{f.slots.danish} DK
                    {f.has_orange_opener && (<span> · Orange: {s?.orange_hit ? "🟢" : "⚪"}</span>)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-score text-4xl">{s?.points ?? 0}</div>
                  <div className="text-xs text-zinc-500">/ {s?.max_points ?? "–"}</div>
                </div>
              </Link>
              {slots.length > 0 && (
                <div className="border-t border-white/5 divide-y divide-white/5">
                  {slots.map((slot, i) => (
                    <SlotRow key={i} slot={slot} artist={artistMap[slot.artist_id]} testId={`slot-${f.id}-${i}`} />
                  ))}
                  {s?.orange_slot && (
                    <SlotRow slot={s.orange_slot} artist={artistMap[s.orange_slot.artist_id]} testId={`orange-slot-${f.id}`} subtitle="Orange Scene åbner" />
                  )}
                </div>
              )}
              {slots.length === 0 && (
                <div className="px-6 py-5 text-zinc-500 text-sm">Ingen valg endnu. <Link to={`/festival/${f.id}`} className="text-white underline">Start her →</Link></div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
